import { formatOdds } from '@/lib/formatters';
import { ArrowUp, ArrowDown, ExternalLink } from 'lucide-react';
import { MARKET_LABELS } from '@/lib/constants';
import { trackBookmakerClick } from '@/api';
import { trackBookmakerClick as trackGTMClick } from '@/lib/analytics';

export default function OddsTable({ odds, bestOdds, bookmakers, market, selectionLabels }) {
  if (!odds?.length || !bookmakers?.length) return null;

  // Use market's defined selection order if available, otherwise derive from odds
  const selections = market?.selections || [...new Set(odds.map(o => o.selection))];
  const labels = selectionLabels || {};

  // Build matrix: bookmaker -> selection -> odds
  const matrix = {};
  bookmakers.forEach(b => { matrix[b.id] = {}; });
  odds.forEach(o => {
    if (matrix[o.bookmaker_id]) {
      matrix[o.bookmaker_id][o.selection] = o;
    }
  });

  // Best odds lookup
  const bestMap = {};
  (bestOdds || []).forEach(b => { bestMap[b.selection] = b; });

  const handleBookmakerClick = (bkm) => {
    trackBookmakerClick(bkm.id, bkm.name, '', 'odds_table').catch(() => {});
    trackGTMClick(bkm.name, '', '', 'odds_table');
    const link = bkm.affiliate_link || bkm.website_url;
    if (link) window.open(link, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="odds-scroll rounded-lg border border-slate-800" data-testid="odds-table">
      <table className="w-full text-sm border-collapse min-w-[500px]">
        <thead>
          <tr className="bg-slate-900/80 border-b border-slate-800">
            <th className="sticky left-0 bg-slate-900 z-10 text-left px-3 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wider w-[140px]">
              Bookmaker
            </th>
            {selections.map(sel => (
              <th key={sel} className="px-3 py-2.5 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider min-w-[80px]">
                {labels[sel] || sel}
              </th>
            ))}
            <th className="px-2 py-2.5 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider w-[40px]">
            </th>
          </tr>
        </thead>
        <tbody>
          {bookmakers.map(bkm => (
            <tr key={bkm.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
              <td className="sticky left-0 bg-slate-950/95 z-[5] px-3 py-2 text-slate-300 font-medium text-xs border-r border-slate-800 truncate max-w-[140px]">
                {bkm.name}
              </td>
              {selections.map(sel => {
                const odd = matrix[bkm.id]?.[sel];
                const isBest = odd?.is_best;
                const prev = odd?.previous_odds;
                const curr = odd?.odds_value;
                const change = curr && prev ? curr - prev : 0;

                return (
                  <td
                    key={sel}
                    className={`px-3 py-2 text-center tabular-nums font-medium cursor-pointer transition-colors
                      ${isBest ? 'bg-emerald-500/15 text-emerald-400 font-bold' : 'text-slate-300 hover:bg-slate-700/30'}
                    `}
                    data-testid={`odds-cell-${bkm.id}-${sel}`}
                  >
                    {odd ? (
                      <div className="flex items-center justify-center gap-1">
                        <span>{formatOdds(curr)}</span>
                        {Math.abs(change) > 0.02 && (
                          change > 0
                            ? <ArrowUp className="w-3 h-3 text-emerald-500" />
                            : <ArrowDown className="w-3 h-3 text-red-500" />
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-600">-</span>
                    )}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-center">
                <button
                  onClick={() => handleBookmakerClick(bkm)}
                  className="text-slate-500 hover:text-emerald-400 transition-colors"
                  title={`Visit ${bkm.name}`}
                  data-testid={`visit-bkm-${bkm.id}`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </td>
            </tr>
          ))}

          {/* Best Odds Row */}
          <tr className="bg-emerald-500/5 border-t-2 border-emerald-500/20">
            <td className="sticky left-0 bg-slate-950 z-[5] px-3 py-2 text-emerald-400 font-bold text-xs border-r border-slate-800 uppercase">
              Best Odds
            </td>
            {selections.map(sel => {
              const best = bestMap[sel];
              return (
                <td key={sel} className="px-3 py-2 text-center">
                  <span className="text-emerald-400 font-bold tabular-nums">{best ? formatOdds(best.best_odd) : '-'}</span>
                  {best && <p className="text-[10px] text-emerald-600 mt-0.5">{best.bookmaker_name}</p>}
                </td>
              );
            })}
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
